'use client';

import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';

import { installErrorHandlers } from '../../src/analytics/error-tracking';
import {
  ensureDomiAuth,
  exchangeDomiSsoCodeFromUrl,
  redirectToDomiLogin,
} from '../../src/auth/domi-auth';
import { installApiFetchPatch } from '../../src/api/client';
import { installWebObservability } from '../../src/observability/install';
import { LOADING_MASCOT_GIF_URL } from '../../src/utils/assets';

// Install browser exception handlers at module-load time, before any other
// client code can throw. The hooks buffer events until AnalyticsProvider
// finishes `bootstrapExceptionTracking()` with the PostHog key, so even
// errors thrown during the dynamic import of `src/App` are captured.
installErrorHandlers();

// Install the rest of the observability surface (long tasks, white-screen
// detector, resource-error capture, boot timing, visibility tracking).
// Same buffer + consent-bypass transport as the exception handler above
// so events fired before AnalyticsProvider initialises still flush.
installWebObservability();

// Domi login gate: on local/test hosts, require domi_token + domi_user in
// localStorage before booting the SPA. Unauthenticated users are sent to the
// agent-base login page with a returnUrl back to this web entrypoint.
const domiAuthState =
  typeof window !== 'undefined' ? ensureDomiAuth() : ('ok' as const);

if (typeof window !== 'undefined' && domiAuthState === 'ok') {
  installApiFetchPatch();
}

// The product is a fully client-driven SPA — every component reads
// localStorage, window.location, etc. — so we opt out of static-time
// rendering for the entire tree. This keeps `next build --output export`
// from trying to evaluate browser-only code while still emitting a real
// shell HTML the daemon can serve as the SPA fallback.
const App = dynamic(() => import('../../src/App').then((m) => m.App), {
  ssr: false,
  loading: () => (
    <div className="od-loading-shell">
      <img className="od-loading-shell__mark" src={LOADING_MASCOT_GIF_URL} alt="" />
    </div>
  ),
});

export function ClientApp() {
  const [authState, setAuthState] = useState(domiAuthState);

  useEffect(() => {
    if (authState !== 'exchanging') return;
    void exchangeDomiSsoCodeFromUrl().then((exchanged) => {
      if (!exchanged) {
        redirectToDomiLogin();
        setAuthState('redirecting');
        return;
      }
      installApiFetchPatch();
      setAuthState('ok');
    });
  }, [authState]);

  if (authState !== 'ok') {
    return <div className="od-loading-shell">Redirecting to login…</div>;
  }
  return <App />;
}
